import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Play, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtMinutes } from "@/lib/format";
import { PageHeader } from "@/components/Page";

function elapsed(startAt) {
  if (!startAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startAt).getTime()) / 1000));
}

function fmtClock(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

export default function Stempeluhr() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [sec, setSec] = useState(0);
  const [busy, setBusy] = useState(false);
  const [today, setToday] = useState([]);
  const timer = useRef(null);

  const load = async () => {
    try {
      const [a, list] = await Promise.all([
        api.get("/sessions/active"),
        api.get("/sessions", { params: { month: new Date().toISOString().slice(0, 7) } }),
      ]);
      setActive(a.data);
      const t = new Date().toISOString().slice(0, 10);
      setToday((list.data || []).filter((s) => s.date === t));
    } catch (e) {
      // ignore transient auth/role errors on initial load
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    clearInterval(timer.current);
    if (active?.start_at) {
      setSec(elapsed(active.start_at));
      timer.current = setInterval(() => setSec(elapsed(active.start_at)), 1000);
    } else {
      setSec(0);
    }
    return () => clearInterval(timer.current);
  }, [active]);

  const clockIn = async () => {
    setBusy(true);
    try {
      await api.post("/sessions/clock-in");
      toast.success("Eingestempelt");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sessions/clock-out");
      toast.success(`Ausgestempelt – ${fmtMinutes(r.data.minutes)} erfasst`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const running = !!active;
  const todayMin = today.reduce((s, x) => s + (x.minutes || 0), 0);

  return (
    <div>
      <PageHeader title={`Hallo, ${user?.name?.split(" ")[0] || ""}`} subtitle="Erfasse deine Arbeitszeit in Echtzeit." testid="stempeluhr-header" />

      <div className="max-w-xl">
        <Card className="p-8 text-center rounded-xl border-slate-200">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className={`h-2.5 w-2.5 rounded-full ${running ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
            <span className={`text-sm font-medium ${running ? "text-emerald-600" : "text-slate-500"}`}>
              {running ? "Läuft" : "Nicht eingestempelt"}
            </span>
          </div>

          <div className="font-mono text-5xl sm:text-6xl font-extrabold tracking-wider text-slate-900" data-testid="live-timer">
            {fmtClock(running ? sec : 0)}
          </div>
          {running && <div className="text-sm text-slate-400 mt-2">Beginn: {active.start} Uhr</div>}

          <div className="mt-8">
            {running ? (
              <Button
                onClick={clockOut}
                disabled={busy}
                data-testid="clock-out-button"
                className="w-full h-14 text-base bg-rose-600 hover:bg-rose-700 text-white"
              >
                {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Square className="h-5 w-5 mr-2" />}
                Ausstempeln
              </Button>
            ) : (
              <Button
                onClick={clockIn}
                disabled={busy}
                data-testid="clock-in-button"
                className="w-full h-14 text-base brand-bg text-white hover:opacity-90"
              >
                {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />}
                Einstempeln
              </Button>
            )}
          </div>
        </Card>

        <Card className="mt-4 p-5 rounded-xl border-slate-200" data-testid="today-summary">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Heute erfasst</span>
            <span className="font-mono text-lg font-bold text-slate-900">{fmtMinutes(todayMin)}</span>
          </div>
          {today.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {today.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm text-slate-600">
                  <span>{s.start} – {s.end || "…"} {s.manual ? "(Nachtrag)" : ""}</span>
                  <span className="font-mono">{fmtMinutes(s.minutes)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
